/* eslint-disable @typescript-eslint/no-explicit-any */
import { defineStore, acceptHMRUpdate } from 'pinia';
import { createFrozenMap } from '@/vuex/utils';
import {
    addGuideToCategory as addGuideToCategoryUtil,
    removeGuideFromCategory as removeGuideFromCategoryUtil,
    fetchGuideCategories,
    fetchGuideCategory,
    createGuideCategory,
    updateGuideCategory,
    deleteGuideCategory,
    sortByName,
    isDraftMetaGroup,
    UNASSIGNED_META_GROUP_ID,
} from '@/vuex/modules/guide-categories/guide-categories.utils';
import { localizeDefaultGuideCategories } from '@/vuex/modules/guide-categories/guide-categories.utils';
import { i18n_t as $t } from '@utils/i18n';
import { useMetaGroupsStore } from '@/stores/meta-groups.store';

import { useProductLinesStore } from '@/stores/product-lines.store';

const loadAllPromise: { promise: Promise<unknown> | null; items: boolean } = {
    promise: null,
    items: false,
};

const getCleanedUpCategory = (category: any, guideId: string) => {
    return {
        ...category,
        items: category?.items.filter((guide: any) => guide.itemId !== guideId) || [],
    };
};

const createUnassignedMetaGroup = (items: unknown[] = []) => ({
    name: $t('metaGroups.Custom'),
    id: UNASSIGNED_META_GROUP_ID,
    description: 'Unassigned categories',
    length: items.length,
    type: 'metaGroup',
    items: sortByName(items),
});

interface GuideCategoriesState {
    map: Record<string, unknown>;
    fetching: boolean;
    saving: boolean;
    error: unknown;
    groupExpandedKeys: unknown[];
}

export function getInitialState (): GuideCategoriesState {
    return {
        map: {},
        fetching: false,
        saving: false,
        error: null,
        groupExpandedKeys: [],
    };
}

export const useGuideCategoriesStore = defineStore('guideCategories', {
    state: getInitialState,
    getters: {
        list: (state) => Object.values(state.map),
        getGroupExpandedKeys: (state) => state.groupExpandedKeys,
        guideIdMapOfCategories (): Record<string, { id: string; name: string }> {
            return (this.list as any[]).reduce(
                (acc: Record<string, { id: string; name: string }>, category: any) => {
                    category?.items.forEach((categoryItem: any) => {
                        if (categoryItem.itemType !== 'Guide') return;

                        acc[categoryItem.itemId] = {
                            id: category.id,
                            name: category.name,
                        };
                    });

                    return acc;
                },
                {},
            );
        },
        guideCategoryById: (state) => (categoryId: string) => state.map[categoryId],
        guideCategoryByGuideId (): ({ guideId }: { guideId?: string }) => unknown {
            return ({ guideId }: { guideId?: string }) => {
                if (!guideId) return undefined;

                return (this.list as any[]).find((category: any) =>
                    category.items.some((item: any) => item.itemId === guideId),
                );
            };
        },
        guideCategoriesGroupedByMetaGroup (): (platform: string) => unknown[] {
            return (platform: string) => {
                const vocEnabled = useProductLinesStore().productLineByType('voc')?.purchased;
                const shouldIncludeCategory = (category: any) => {
                    if (!category) return false;
                    if (category.attributes?.platform !== platform) return false;
                    if (category.name === 'Idea Test' && !vocEnabled) return false;

                    return true;
                };
                const groupedCategoryIds = new Set<string>();

                const metaGroupsWithCategories = sortByName(useMetaGroupsStore().list).map(
                    (metaGroup: any) => {
                        const items = metaGroup.items
                            .map((item: any) => {
                                groupedCategoryIds.add(item.itemId);

                                return (this.map as any)[item.itemId];
                            })
                            .filter(shouldIncludeCategory);

                        return { ...metaGroup, items: sortByName(items) };
                    },
                );

                const unassignedCategories = (this.list as any[]).filter(
                    (category: any) =>
                        !groupedCategoryIds.has(category.id) && shouldIncludeCategory(category),
                );
                const unassignedMetaGroup = createUnassignedMetaGroup(unassignedCategories);

                metaGroupsWithCategories.push(unassignedMetaGroup);

                return metaGroupsWithCategories;
            };
        },
        getGuideCategoryByName (): (name: string, platform?: string) => unknown {
            return (name: string, platform = 'web') => {
                return (this.list as any[]).find(
                    (category: any) =>
                        category.name === name && category.attributes.platform === platform,
                );
            };
        },
    },
    actions: {
        setMap ({ map }: { map: Record<string, unknown> }) {
            this.map = map;
        },
        setMapAtKey ({ guideCategory }: { guideCategory: any }) {
            this.map[guideCategory.id] = guideCategory;
        },
        unsetMapAtKey (id: string) {
            delete this.map[id];
        },
        setError ({ error }: { error: unknown }) {
            this.error = error;
        },
        setFetching ({ fetching }: { fetching: boolean }) {
            this.fetching = fetching;
        },
        setSaving ({ saving }: { saving: boolean }) {
            this.saving = saving;
        },
        setPairedCategories (categories: any[]) {
            categories.forEach((category) => {
                if (category.type === 'guideCategory') {
                    this.map[category.id] = category;
                }
            });
        },
        setGroupExpandedKeys ({ groupExpandedKeys }: { groupExpandedKeys: unknown[] }) {
            this.groupExpandedKeys = groupExpandedKeys;
        },
        async loadAll ({
            reFetch = false,
            items = false,
        }: { reFetch?: boolean; items?: boolean } = {}) {
            if (loadAllPromise.promise) {
                await loadAllPromise.promise;

                if (!reFetch && (loadAllPromise.items || !items)) {
                    return;
                }
            }
            this.setFetching({ fetching: true });
            loadAllPromise.items = items;
            loadAllPromise.promise = fetchGuideCategories({ items });

            try {
                const { data: categories } = await loadAllPromise.promise as any;

                const localizedCategories = localizeDefaultGuideCategories(categories);
                this.setFetching({ fetching: false });
                this.setMap({
                    map: createFrozenMap(localizedCategories) as Record<string, unknown>,
                });
            } catch (error) {
                loadAllPromise.promise = null;
                this.setFetching({ fetching: false });
                this.setError({ error });
                throw error;
            }
        },
        async loadOne ({
            reFetch = false,
            items = false,
            categoryId,
        }: {
            reFetch?: boolean;
            items?: boolean;
            categoryId: string;
        }) {
            if (!reFetch && !!this.guideCategoryById(categoryId)) {
                return;
            }

            this.setFetching({ fetching: true });

            try {
                const { data: guideCategory } = await fetchGuideCategory({ categoryId, items });
                const localizedGuideCategory = localizeDefaultGuideCategories([guideCategory])[0];
                this.setFetching({ fetching: false });
                this.setMapAtKey({
                    guideCategory: Object.freeze(localizedGuideCategory),
                });
            } catch (error) {
                this.setFetching({ fetching: false });
                this.setError({ error });
                throw error;
            }
        },
        async createCategory (payload: Record<string, unknown> = {}) {
            this.setFetching({ fetching: true });

            try {
                const { data: categories } = await createGuideCategory({
                    type: 'guideCategory',
                    ...payload,
                });

                this.setPairedCategories(categories);

                return categories;
            } catch (error) {
                this.setError({ error });
                throw error;
            } finally {
                this.setFetching({ fetching: false });
            }
        },
        async updateCategory (payload: Record<string, unknown> = {}) {
            this.setFetching({ fetching: true });

            try {
                const { data: categories } = await updateGuideCategory(payload);

                this.setPairedCategories(categories);

                return categories;
            } catch (error) {
                this.setError({ error });
                throw error;
            } finally {
                this.setFetching({ fetching: false });
            }
        },
        async deleteCategory (id: string) {
            this.setFetching({ fetching: true });

            try {
                await deleteGuideCategory(id, 'guideCategory');

                const toRemove = this.guideCategoryById(id) as any;
                const deleted: unknown[] = [];

                if (toRemove) {
                    const toRemoveName = toRemove.name.toLowerCase();
                    (this.list as any[]).forEach((category: any) => {
                        if (category.name.toLowerCase() === toRemoveName) {
                            this.unsetMapAtKey(category.id);
                            deleted.push(category);
                        }
                    });
                }

                return deleted;
            } catch (error) {
                this.setError({ error });
                throw error;
            } finally {
                this.setFetching({ fetching: false });
            }
        },
        async addGuideToCategory (payload: Record<string, unknown> = {}) {
            this.setSaving({ saving: true });
            const { categoryId, guideId } = payload as { categoryId?: string; guideId?: string };

            if (!categoryId || !guideId) {
                this.setSaving({ saving: false });

                return;
            }

            try {
                if (payload.oldCategoryId && this.map[payload.oldCategoryId as string]) {
                    const cleanedUpCategory = getCleanedUpCategory(
                        this.map[payload.oldCategoryId as string],
                        payload.guideId as string,
                    );
                    this.setMapAtKey({
                        guideCategory: Object.freeze(cleanedUpCategory),
                    });
                }
                await addGuideToCategoryUtil(payload);
                await this.loadOne({ reFetch: true, items: true, categoryId });
            } catch (error) {
                this.setError({ error });
                throw error;
            } finally {
                this.setSaving({ saving: false });
            }
        },
        async removeGuideFromCategory (payload: Record<string, unknown> = {}) {
            this.setSaving({ saving: true });
            const { categoryId, guideId } = payload as { categoryId?: string; guideId?: string };
            if (!categoryId || !guideId) {
                this.setSaving({ saving: false });

                return;
            }

            try {
                await removeGuideFromCategoryUtil(payload);
                this.setMapAtKey({
                    guideCategory: Object.freeze(
                        getCleanedUpCategory(
                            this.map[payload.categoryId as string],
                            payload.guideId as string,
                        ),
                    ),
                });
            } catch (error) {
                this.setError({ error });
                throw error;
            } finally {
                this.setSaving({ saving: false });
            }
        },
        async assignCategoryToMetaGroup ({
            categoryId,
            selectedMetaGroup,
            originalMetaGroupId,
        }: {
            categoryId: string;
            selectedMetaGroup?: any;
            originalMetaGroupId?: string;
        }) {
            const newMetaGroupId = selectedMetaGroup?.id;

            if (newMetaGroupId === originalMetaGroupId) {
                return { failedStep: '' };
            }

            let currentStep = '';
            try {
                if (originalMetaGroupId) {
                    currentStep = 'removeFromOld';
                    await useMetaGroupsStore().removeCategoryFromMetaGroup({
                        metaGroupId: originalMetaGroupId,
                        categoryId,
                    });
                }

                let targetMetaGroupId = newMetaGroupId;
                if (newMetaGroupId && isDraftMetaGroup(selectedMetaGroup)) {
                    currentStep = 'createNewGroup';
                    const created = await useMetaGroupsStore().createMetaGroup({ name: selectedMetaGroup.name });
                    targetMetaGroupId = created.id;
                }

                if (newMetaGroupId) {
                    currentStep = 'addToNew';
                    await useMetaGroupsStore().addCategoryToMetaGroup({
                        metaGroupId: targetMetaGroupId,
                        categoryId,
                    });
                }

                return { failedStep: '' };
            } catch (error) {
                console.error(`Error in ${currentStep}:`, error);

                return { failedStep: currentStep };
            }
        },
    },
});

export function __resetLoadAllPromiseForUnitTests () {
    loadAllPromise.promise = null;
    loadAllPromise.items = false;
}

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useGuideCategoriesStore, module.hot));
}
